import * as d3 from 'd3';

export interface NumberLineOptions {
  container: HTMLElement;
  min: number;
  max: number;
  initial: readonly number[];
  height?: number;
  onChange(points: number[]): void;
}

export interface NumberLineHandle {
  element: SVGSVGElement;
  setPoints(points: readonly number[]): void;
  getPoints(): readonly number[];
  destroy(): void;
}

export function createNumberLine(opts: NumberLineOptions): NumberLineHandle {
  let points: number[] = [...opts.initial];
  const width = opts.container.clientWidth || 600;
  const height = opts.height ?? 120;
  const margin = { left: 30, right: 30 };

  const svg = d3
    .create('svg')
    .attr('class', 'number-line')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .node()!;
  opts.container.appendChild(svg);

  const root = d3.select(svg).append('g');
  const x = d3
    .scaleLinear()
    .domain([opts.min, opts.max])
    .range([margin.left, width - margin.right]);

  root
    .append('line')
    .attr('class', 'np-axis')
    .attr('x1', x(opts.min))
    .attr('x2', x(opts.max))
    .attr('y1', height / 2)
    .attr('y2', height / 2);

  const axis = d3.axisBottom(x).ticks(10);
  root
    .append('g')
    .attr('class', 'np-ticks')
    .attr('transform', `translate(0,${height / 2})`)
    .call(axis);

  // Hit rect appended BEFORE the points layer so points paint above it and
  // intercept pointer events aimed at them (fixes the prior z-order bug,
  // where the rect — appended last — shadowed point hover/drag).
  const hit = root
    .append('rect')
    .attr('class', 'np-hit')
    .attr('x', margin.left)
    .attr('y', 0)
    .attr('width', width - margin.left - margin.right)
    .attr('height', height)
    .attr('fill', 'transparent');

  const layer = root.append('g').attr('class', 'np-points');

  // "Add" ghost affordance: dashed circle + '+' following the pointer along
  // the axis while hovering the hit rect. Decorative only (pointer-events
  // disabled) so it never steals events from the rect/points beneath it.
  const ghost = root
    .append('g')
    .attr('class', 'np-ghost')
    .attr('pointer-events', 'none')
    .style('display', 'none');
  ghost
    .append('circle')
    .attr('r', 8)
    .attr('cy', height / 2);
  ghost
    .append('text')
    .attr('y', height / 2)
    .text('+');

  // "Remove" hover affordance: a single '−' glyph repositioned onto whichever
  // point is currently hovered.
  const minus = root
    .append('text')
    .attr('class', 'np-minus')
    .attr('y', height / 2)
    .style('display', 'none')
    .text('−');

  function render() {
    const sel = layer.selectAll<SVGCircleElement, number>('circle.np-point').data(points);
    sel.exit().remove();
    const entered = sel
      .enter()
      .append('circle')
      .attr('class', 'np-point')
      .attr('r', 8)
      .attr('cy', height / 2);
    entered.merge(sel).attr('cx', (d) => x(d));

    layer
      .selectAll<SVGCircleElement, number>('circle.np-point')
      .on('contextmenu', function (event: MouseEvent) {
        event.preventDefault();
        const idx = layer.selectAll('circle.np-point').nodes().indexOf(this);
        if (idx >= 0) {
          points.splice(idx, 1);
          opts.onChange([...points]);
          minus.style('display', 'none');
          render();
        }
      })
      .on('pointerenter', function () {
        const cx = d3.select(this).attr('cx');
        minus.attr('x', cx).style('display', null);
      })
      .on('pointerleave', () => {
        minus.style('display', 'none');
      })
      .call(
        d3
          .drag<SVGCircleElement, number>()
          .on('drag', function (event: d3.D3DragEvent<SVGCircleElement, number, unknown>) {
            const idx = layer.selectAll('circle.np-point').nodes().indexOf(this);
            if (idx < 0) return;
            const v = Math.max(opts.min, Math.min(opts.max, x.invert(event.x)));
            points[idx] = v;
            d3.select(this).attr('cx', x(v));
          })
          .on('end', () => opts.onChange([...points])),
      );
  }

  hit
    .on('click', (event: MouseEvent) => {
      const v = x.invert(d3.pointer(event)[0]);
      points.push(v);
      opts.onChange([...points]);
      render();
    })
    .on('pointermove', (event: PointerEvent) => {
      const px = d3.pointer(event)[0];
      ghost.attr('transform', `translate(${px},0)`).style('display', null);
    })
    .on('pointerleave', () => {
      ghost.style('display', 'none');
    });

  render();

  return {
    element: svg,
    setPoints(next) {
      points = [...next];
      render();
      opts.onChange([...points]);
    },
    getPoints: () => [...points],
    destroy() {
      svg.remove();
    },
  };
}
